import Vue from 'vue';
import firebase, { database, functions } from '@/plugins/firebase';
import Instructor from "@/classes/Instructor";
import { displayName, nameWithNickname } from "@/utils/instructorName";
import store from "@/store";
import _ from "lodash";
import UnresolvedPayment from "@/classes/UnresolvedPayment";
import { logActivity } from '@/services/ActivityLogService';
import moment from 'moment';
import { callFn } from '@/utils/functionsClient';
export const InstructorsCollection = 'instructors';
/** Only the display fields are known; the rest fills the profile the type expects. */
function toInstructorData(instructor) {
    return {
        name: instructor.name,
        nickname: instructor.nickname,
        surname: instructor.surname,
        image: instructor.image,
        email: '',
        phone: null,
        grade_id: null,
        role: 0,
        notifications: [],
        balance: 0,
        paysTaxes: false,
    };
}
export const InstructorModule = {
    namespaced: true,
    state: {
        list: [],
        initialLoadComplete: false,
    },
    mutations: {
        store(state, instructorObject) {
            const instructor = new Instructor(instructorObject);
            const x = state.list.find(x => x.id === instructor.id);
            if (x) {
                if (_.isEqual(x, instructor)) {
                    return;
                }
                state.list.splice(state.list.indexOf(x), 1, instructor);
                store.dispatch('instructor/storeCache');
                return;
            }
            state.list.push(instructor);
            store.dispatch('instructor/storeCache');
        },
        update(state, instructor) {
            state.list.find((oldInstructor, index) => {
                if (oldInstructor.id === instructor.id) {
                    Vue.set(state.list, index, new Instructor(instructor));
                }
            });
        },
        destroy(state, instructor) {
            const index = state.list.findIndex((item) => instructor.id === item.id);
            if (index !== -1) {
                Vue.set(state.list[index].data, 'deletedAt', instructor.data.deletedAt);
            }
        },
        clear(state) {
            state.list.splice(0, state.list.length);
            state.initialLoadComplete = false;
        },
        setInitialLoadComplete(state) {
            state.initialLoadComplete = true;
        },
    },
    actions: {
        async store(context, instructor) {
            // get cloud functions
            const createAuth = functions.httpsCallable('createAuth');
            const setRole = functions.httpsCallable('setRole');
            // create authentication on firebase
            const auth = await createAuth({
                email: instructor.data.email,
            });
            // set user role (adds custom claim to authentication)
            await setRole({
                uid: auth.data.uid,
                role: instructor.data.role,
            });
            // send password reset email
            await firebase.auth().sendPasswordResetEmail(instructor.data.email);
            // create user associated document on firestore
            const doc = await database
                .collection(InstructorsCollection)
                .doc(auth.data.uid);
            await doc.set(instructor.data);
            // Log activity (fire-and-forget)
            logActivity('CREATE', 'instructor', auth.data.uid, null, instructor.data);
        },
        async update(context, instructor) {
            // set user role (sets custom claim to authentication)
            const setRole = functions.httpsCallable('setRole');
            await setRole({
                uid: instructor.id,
                role: instructor.data.role,
            });
            // update user associated document on firestore
            let document = await database
                .collection(InstructorsCollection)
                .doc(instructor.id);
            const { balance, ...updateData } = instructor.data;
            await document.update(updateData);
            // Log activity (fire-and-forget) - note: we don't have previous value readily available
            logActivity('UPDATE', 'instructor', instructor.id, null, instructor.data);
        },
        async updateUser(context, formData) {
            // update user associated document on firestore
            let document = await database
                .collection(InstructorsCollection)
                .doc(formData.id);
            const { image, name, nickname, surname, notifications, bankAccount, address, birthNumber, ico } = formData;
            await document.update({ name, nickname, surname, image, notifications, bankAccount, address, birthNumber, ico });
        },
        /**
         * Assign a grade to an instructor with historical tracking.
         * Updates both the current grade_id and adds an entry to gradeHistory.
         *
         * @param payload.instructorId - ID of the instructor to update
         * @param payload.gradeId - ID of the grade to assign
         * @param payload.effectiveDate - Optional ISO date string (defaults to today)
         */
        async assignGrade(context, payload) {
            const { instructorId, gradeId, effectiveDate = moment().format('YYYY-MM-DD') } = payload;
            // Validate effectiveDate against last concluded work date
            const { validateEffectiveDateOrThrow } = await import('@/utils/effectiveDateValidation');
            await validateEffectiveDateOrThrow(effectiveDate);
            const instructor = context.getters.get(instructorId);
            if (!instructor) {
                throw new Error(`Instruktor s ID "${instructorId}" nebyl nalezen.`);
            }
            // Build the updated grade history
            const currentHistory = instructor.data.gradeHistory || [];
            const newAssignment = {
                gradeId,
                effectiveDate,
            };
            const updatedHistory = [...currentHistory, newAssignment];
            // Update both grade_id and gradeHistory in Firestore
            const doc = database.collection(InstructorsCollection).doc(instructorId);
            await doc.update({
                grade_id: gradeId,
                gradeHistory: updatedHistory,
            });
            // Log activity (fire-and-forget)
            logActivity('UPDATE', 'instructor', instructorId, null, {
                grade_id: gradeId,
                gradeHistory: updatedHistory,
            });
        },
        async destroy(context, instructor) {
            // Log activity (fire-and-forget) - log before soft deletion
            logActivity('DELETE', 'instructor', instructor.id, instructor.data, null);
            // Soft delete - set deletedAt timestamp instead of deleting
            instructor.data.deletedAt = firebase.firestore.Timestamp.now();
            // Update document with deletedAt timestamp (soft delete)
            const doc = database.collection(InstructorsCollection).doc(instructor.id);
            await doc.update({ deletedAt: instructor.data.deletedAt });
            // Call Cloud Function to revoke tokens and disable auth (non-blocking)
            if (instructor.data.email) {
                try {
                    const softDeleteUser = functions.httpsCallable('softDeleteUser');
                    await softDeleteUser({ uid: instructor.id });
                }
                catch (error) {
                    console.error('Failed to revoke user session:', error);
                    // Non-blocking - Firestore update is the primary operation
                }
            }
            // Update local state and cache
            context.commit('destroy', instructor);
            context.dispatch('storeCache');
        },
        /**
         * An instructor may read only their own profile, so the crew's names come from the
         * server and the live document is the user's own.
         */
        async listenOwn(context) {
            const names = await callFn('listInstructorNames', {});
            for (const instructor of names) {
                context.commit('store', { id: instructor.id, data: toInstructorData(instructor) });
            }
            return database
                .collection(InstructorsCollection)
                .doc(store.getters['user/userId'])
                .onSnapshot((doc) => {
                if (doc.exists) {
                    context.commit('store', { id: doc.id, data: doc.data() });
                    context.commit('update', { id: doc.id, data: doc.data() });
                }
                context.commit('setInitialLoadComplete');
            });
        },
        async listen(context) {
            context.commit('clear');
            //await context.dispatch('loadCache');
            if (!store.getters['user/isModerator']) {
                return context.dispatch('listenOwn');
            }
            let isFirstSnapshot = true;
            return database
                .collection(InstructorsCollection)
                .onSnapshot((snapshot) => {
                for (let change of snapshot.docChanges()) {
                    switch (change.type) {
                        case 'added':
                            context.commit('store', {
                                id: change.doc.id,
                                data: change.doc.data(),
                            });
                            break;
                        case 'modified':
                            context.commit('update', {
                                id: change.doc.id,
                                data: change.doc.data(),
                            });
                            context.dispatch('storeCache');
                            break;
                        case 'removed':
                            context.commit('destroy', {
                                id: change.doc.id,
                                data: change.doc.data(),
                            });
                            context.dispatch('storeCache');
                            break;
                    }
                }
                // Mark initial load as complete after processing first snapshot
                if (isFirstSnapshot) {
                    context.commit('setInitialLoadComplete');
                    isFirstSnapshot = false;
                }
            });
        },
        async storeCache(context) {
            localStorage.removeItem(InstructorsCollection);
            try {
                localStorage.setItem(InstructorsCollection, JSON.stringify(context.state.list));
            }
            catch (e) { }
        },
        async loadCache(context) {
            const cache = localStorage.getItem(InstructorsCollection);
            if (!cache) {
                return;
            }
            const instructors = JSON.parse(cache);
            for (const instructor of instructors) {
                context.commit('store', { id: instructor.id, data: instructor.data });
            }
        },
    },
    getters: {
        // Check if initial data load is complete (for E2E tests)
        isLoaded(state) {
            return state.initialLoadComplete;
        },
        // Active entities only (for selection dropdowns) - deletedAt is null, undefined, or missing
        active(state) {
            return state.list
                .filter((i) => !i.data.deletedAt)
                .sort((a, b) => a.data.surname.localeCompare(b.data.surname));
        },
        // All entities including deleted (for historical views)
        all(state) {
            return state.list;
        },
        // Deleted entities only (for admin filter)
        deleted(state) {
            return state.list
                .filter((i) => !!i.data.deletedAt)
                .sort((a, b) => a.data.surname.localeCompare(b.data.surname));
        },
        // Active first, then deleted (for management list per FR-010)
        allSortedForManagement(state) {
            const active = state.list
                .filter((i) => !i.data.deletedAt)
                .sort((a, b) => a.data.surname.localeCompare(b.data.surname));
            const deleted = state.list
                .filter((i) => !!i.data.deletedAt)
                .sort((a, b) => a.data.surname.localeCompare(b.data.surname));
            return [...active, ...deleted];
        },
        allUnresolvedPayments(state) {
            return state.list
                .filter((instructor) => instructor.data.balance !== 0)
                .map((instructor) => new UnresolvedPayment(instructor));
        },
        fullNames(state) {
            return state.list.map((x) => ({
                id: x.id,
                name: displayName(x.data),
                fullName: nameWithNickname(x.data),
                avatar: x.data.image,
            }));
        },
        get(state) {
            return (id) => state.list.find(instructor => instructor.id === id);
        },
    },
};
